import {Card} from "../../../../../../components/card/card.jsx";
import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import Alert from "../../../../../../components/alert/alert.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import FormField from "../../../../../../components/react-hook-form/form-field.jsx";
import {EXTRA_SETTINGS_RULES, SMART_LINE_DEFAULTS, triggerTypes} from "../../../utils/lanes-form-utils.js";
import {useFormContext} from "react-hook-form";
import InputForm from "../../../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../../../components/input/input-checkbox.jsx";
import SettingsCanvas from "./settings-canvas.jsx";
import {useCallback, useEffect} from "react";

const Settings = ({setup, dataInfo}) => {
  const {watch, setValue, getValues} = useFormContext();

  const model = watch(`smartcaptureDriver.${setup}.model`);
  const trigger = watch(`smartcaptureDriver.${setup}.smartLooptype`);
  const downX1 = watch(`smartcaptureDriver.${setup}.model_details.smartLineDownX1`) ?? 36;
  const downX2 = watch(`smartcaptureDriver.${setup}.model_details.smartLineDownX2`) ?? 100;
  const upX1 = watch(`smartcaptureDriver.${setup}.model_details.smartLineUpX1`) ?? 36;
  const upX2 = watch(`smartcaptureDriver.${setup}.model_details.smartLineUpX2`) ?? 100;

  useEffect(() => {
    if(trigger !== "smart") return;
    const base = `smartcaptureDriver.${setup}.model_details`;

    Object.entries(SMART_LINE_DEFAULTS).forEach(([key, defaultValue]) => {
      const current = getValues(`${base}.${key}`);
      if(current === null || current === undefined || current === ""){
        setValue(`${base}.${key}`, defaultValue, {shouldDirty: true});
      }
    });
  }, [trigger, setup, setValue, getValues]);

  const normalizeNumberValue = useCallback((value, min, max) => {
    if(value === "" || value === null || value === undefined) return value;

    const numberValue = Number(value);
    if(Number.isNaN(numberValue)) return value;
    if(min !== undefined && numberValue < min) return min;
    if(max !== undefined && numberValue > max) return max;

    return value;
  }, [])

  const NumberInputForm = useCallback(({min, max, onChange, ...props}) => (
    <InputForm
      {...props}
      min={min}
      max={max}
      type="number"
      onChange={({target}) => onChange?.(normalizeNumberValue(target.value, min, max))}
    />
  ), [normalizeNumberValue])


  const createTable = (data, type) => {
    if(type === "isDayNight"){
      return data.map(el =>
        <tr key={el.label} className="text-[var(--default-color)]">
          <th className="border border-[var(--default-border)] border-collapse p-1">{el.label}</th>
          <td className="border border-[var(--default-border)] border-collapse">
            <FormField
              name={`smartcaptureDriver.${setup}.model_details.${el.dayKey}`}
              as={NumberInputForm}
              id={`smartcaptureDriver${setup}${el.dayKey}Form`}
              type="number"
              classNameWrapper="p-1"
              min={el.minValue ?? undefined}
              max={el.maxValue ?? undefined}
            />
          </td>
          <td className="border border-[var(--default-border)] border-collapse">
            <FormField
              name={`smartcaptureDriver.${setup}.model_details.${el.nightKey}`}
              as={NumberInputForm}
              id={`smartcaptureDriver${setup}${el.nightKey}Form`}
              type="number"
              classNameWrapper="p-1"
              disabled={el?.isDisabled?.("noite", model)}
              min={el.minValue ?? undefined}
              max={el.maxValue ?? undefined}
            />
          </td>
        </tr>
      )
    }
    if(type === "uniqueValue"){
        return data.map(el =>
          <tr key={el.label} className="text-[var(--default-color)]">
            <th className="border border-[var(--default-border)] border-collapse p-1">{el.label}</th>
            <td colSpan={2} className="border border-[var(--default-border)] border-collapse">
              <FormField
                name={`smartcaptureDriver.${setup}.model_details.${el.key}`}
                as={NumberInputForm}
                id={`smartcaptureDriver${setup}${el.key}Form`}
                type="number"
                classNameWrapper="p-1"
                disabled={el?.isDisabled?.(model, trigger)}
                min={el.minValue ?? undefined}
                max={el.maxValue ?? undefined}
              />
            </td>
          </tr>
        )
    }
    if(type === "isSelect"){
      return data.map(el =>
        <tr key={el.label} className="text-[var(--default-color)]">
          <th className="border border-[var(--default-border)] border-collapse p-1">{el.label}</th>
          <td colSpan={2} className="border border-[var(--default-border)] border-collapse p-1">
            <FormField
              name={`smartcaptureDriver.${setup}.model_details.${el.key}`}
              customRender={field => (
                <SelectComponent
                  id={`smartcaptureDriver.${setup}${el.key}Form`}
                  instanceId={`smartcaptureDriver${setup}${el.key}Form`}
                  options={el.options}
                  value={el.options.find(el => el.value === field.value)}
                  onChange={selected => field.onChange(selected?.value)}
                  isDisabled={el?.isDisabled(model, trigger)}
                />
              )}
            />
          </td>
        </tr>
      )
    }
    if(type === "check"){
      return data.map(el =>
        <tr key={el.label} className="text-[var(--default-color)]">
          <th colSpan={el.hasCheckAndValue ? 1 : 3} className="border border-[var(--default-border)] border-collapse p-1">
            <div className="flex gap-2 items-center">
              <FormField
                name={`smartcaptureDriver.${setup}.model_details.${el.key}`}
                customRender={field => (
                  <InputCheckbox
                    checked={field.value}
                    onChange={() => field.onChange(!field.value)}
                    disabled={el?.isDisabled?.(model, trigger)}
                  />
                )}
              />
              {el.label}
            </div>
          </th>
          { el.hasCheckAndValue &&
            <td colSpan={2} className="border border-[var(--default-border)] border-collapse">
              <FormField
                name={`smartcaptureDriver.${setup}.model_details.${el.hasCheckAndValue ? el.key2 : el.key}`}
                as={NumberInputForm}
                id={`smartcaptureDriver${setup}${el.hasCheckAndValue ? el.key2 : el.key}Form`}
                type="number"
                classNameWrapper="p-1"
                disabled={!watch(`smartcaptureDriver.${setup}.model_details.${el.key}`)}
                min={el.minValue ?? undefined}
                max={el.maxValue ?? undefined}
              />
            </td>
          }
        </tr>
      )
    }
  }

  const getMax = (x1, x2, minAllowedValue) => {
    if((x2 - x1) > minAllowedValue){
      return;
    }
    return x1
  }

  const getMin = (x1, x2, minAllowedValue) => {
    if(x2 - x1 > minAllowedValue){
      return;
    }
    return x2
  }

  return(
    <Card>
      <Card.Body>
        <SentryRow>
          <div className="col-span-6">
            <FormField
              name={`smartcaptureDriver.${setup}.smartLooptype`}
              customRender={field => (
                <SelectComponent
                  id={`${setup}SettingsTriggerTypeForm`}
                  instanceId={`${setup}SettingsTriggerTypeForm`}
                  label="Tipo de gatilho"
                  isClearable={false}
                  options={triggerTypes.filter(el => el.availableOn.includes(model))}
                  onChange={selected => {
                    field.onChange(selected?.value);
                    if(selected?.value === "smart") {
                      Object.entries(SMART_LINE_DEFAULTS).forEach(([key, value]) =>
                        setValue(`smartcaptureDriver.${setup}.model_details.${key}`, value, {shouldDirty: true})
                      );
                    }
                  }}
                  value={triggerTypes.find(el => el.value === field.value)}
                />
              )}
            />
            { (trigger ?? "") &&
              <table className="mt-2">
                <thead className="thead-background">
                <tr>
                  <th className="w-[63%] border border-[var(--default-border)] border-collapse p-1"></th>
                  <th className="w-[16%] border border-[var(--default-border)] border-collapse text-[var(--default-color)] p-1">Dia</th>
                  <th className="w-[21%] border border-[var(--default-border)] border-collapse text-[var(--default-color)] p-1">Noite</th>
                </tr>
                </thead>
                <tbody>
                {createTable(EXTRA_SETTINGS_RULES.filter(el => el.isDayNight), "isDayNight")}
                {createTable(EXTRA_SETTINGS_RULES.filter(el => el.isUniqueFirst), "uniqueValue")}
                {createTable(EXTRA_SETTINGS_RULES.filter(el => el.isSelect), "isSelect")}
                {createTable(EXTRA_SETTINGS_RULES.filter(el => el.isUniqueSecond), "uniqueValue")}
                {createTable(EXTRA_SETTINGS_RULES.filter(el => el.hasCheckAndValue), "check")}
                {createTable(EXTRA_SETTINGS_RULES.filter(el => el.onlyCheckBox), "check")}
                </tbody>
              </table>
            }
          </div>
          <div className="col-span-6">
            <Card>
              <Card.Header>
                <Card.Title>
                  Gatilho virtual
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {trigger !== "smart"
                  ? <Alert target="warning">
                      Indisponível
                    </Alert>
                  : <div className="h-[320px] flex gap-2">
                      <div className="flex flex-col justify-around w-[60px]">
                        <FormField
                          name={`smartcaptureDriver.${setup}.model_details.smartLineUpX1`}
                          as={NumberInputForm}
                          type="number"
                          min={0}
                          max={getMax(upX1,upX2, 40)}
                        />
                        <FormField
                          name={`smartcaptureDriver.${setup}.model_details.smartLineDownX1`}
                          as={NumberInputForm}
                          type="number"
                          min={0}
                          max={getMax(downX1,downX2, 50)}
                        />
                      </div>
                      <div className="h-full flex-1 min-w-0 border border-[#ddd]">
                        <SettingsCanvas setup={setup} dataInfo={dataInfo}/>
                      </div>
                      <div className="flex flex-col justify-around w-[60px]">
                        <FormField
                          name={`smartcaptureDriver.${setup}.model_details.smartLineUpX2`}
                          as={NumberInputForm}
                          type="number"
                          max={100}
                          min={getMin(upX1, upX2, 40)}
                        />
                        <FormField
                          name={`smartcaptureDriver.${setup}.model_details.smartLineDownX2`}
                          as={NumberInputForm}
                          type="number"
                          max={100}
                          min={getMin(downX1, downX2, 50)}
                        />
                      </div>
                    </div>
                }
              </Card.Body>
            </Card>
          </div>
        </SentryRow>
      </Card.Body>
    </Card>
  )
}

export default Settings;